import { ref } from 'vue';
import { useEventListener } from '@vueuse/core';
import { sentry } from '/app/plugins/sentry';
import { config } from '/app/config';

/**
 * Client half of the geo/maintenance gate.
 *
 * The authoritative check lives in the Netlify edge function, but it only runs
 * on a full document request. This app is an SPA: after boot, every navigation
 * is client-side, and a tab can stay open for days. So the edge verdict is
 * re-read from /__gate on boot and whenever the tab regains focus, and a
 * revoked verdict is turned back into a document request — which the edge then
 * answers with the block page.
 */

const ENDPOINT = '/__gate';

// Reloading is how we hand control back to the edge, so a gate that answers
// `blocked` on every request would loop forever if the reload itself somehow
// still rendered the app (stale document in a cache we can't reach). One
// attempt per tab session is enough to prove the reload didn't help.
const RELOAD_GUARD_KEY = 'bn:gate:reloaded';

// Focus fires constantly (alt-tab, devtools, autofill). Only re-check when the
// tab was actually away long enough for geo or config to plausibly change.
const RECHECK_AFTER_MS = 5 * 60 * 1000;

export const useAccessGate = () => {
    const blocked = ref(false);
    let lastCheckedAt = 0;
    let inFlight = null;

    async function fetchStatus() {
        const response = await fetch(ENDPOINT, {
            cache: 'no-store',
            credentials: 'include',
            headers: { accept: 'application/json' },
        });

        // In dev there is no edge function: the SPA fallback answers with
        // index.html, which must not be read as a verdict.
        const type = response.headers.get('content-type') || '';
        if (!response.ok || !type.includes('application/json')) {
            return null;
        }

        return response.json();
    }

    /**
     * Re-read the edge verdict. Fails open: a network blip or an unreachable
     * endpoint must never lock out a legitimate visitor, because the next real
     * document request is gated regardless.
     */
    async function check({ force = false } = {}) {
        if (config.app.isDev) return;
        if (inFlight) return inFlight;
        if (!force && Date.now() - lastCheckedAt < RECHECK_AFTER_MS) return;

        inFlight = (async () => {
            try {
                const status = await fetchStatus();
                lastCheckedAt = Date.now();

                if (!status?.blocked) return;

                blocked.value = true;

                if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return;
                sessionStorage.setItem(RELOAD_GUARD_KEY, '1');

                // The edge owns the block/maintenance UI; reloading renders it
                // instead of duplicating those pages in the SPA.
                window.location.reload();
            } catch (error) {
                sentry.addBreadcrumb({
                    category: 'App',
                    message: 'Access gate check failed',
                    level: 'warning',
                });
            } finally {
                inFlight = null;
            }
        })();

        return inFlight;
    }

    function watchVisibility() {
        useEventListener(document, 'visibilitychange', () => {
            if (document.visibilityState === 'visible') check();
        });

        // bfcache restores skip visibilitychange in some browsers.
        useEventListener(window, 'pageshow', (event) => {
            if (event.persisted) check({ force: true });
        });
    }

    return {
        blocked,
        check,
        watchVisibility,
    };
};
